// The aim is to deliver a soft parsing experience, in which invalid query strings deliver an empty result
import {QUERY_ENTRY_SEPARATOR, URL_FILTER_PARAM} from '../const';

import {parseURI} from '../search.utils';
import {
    EQUALS,
    HibuEqualsFilter,
    HibuFacetFilter,
    HibuFacetGroup,
    HibuFilter,
    HibuRangeFilter,
    RANGE
} from '../../facets/facet.types';

const QUERY_PARAM_REG_EXP = /([a-zA-Z\d._]+)\[(.+)]/;

/**
 * Parses string of the form expressed below in the example.
 * @param {string} facetUrlParams
 * @return HibuFacetFilter
 * @example
 * const q_params = parseFacetFilterString('doctype[pdf|word]lang[de, |en],person[!max%20smith|steve]&s=title')
 */
export const parseFiltersFromUrl = (facetUrlParams?: string): HibuFacetFilter => {
    const filters: HibuFacetFilter = {};

    if (!facetUrlParams) {
        return filters;
    }
    for (const facetUrlParam of decodeURI(facetUrlParams).split(QUERY_ENTRY_SEPARATOR)) {
        const matches = facetUrlParam.match(QUERY_PARAM_REG_EXP);
        const queryParamName = matches ? matches[1] : null;
        if (queryParamName) {
            if (!filters[queryParamName]) {
                filters[queryParamName] = [];
            }
            const queryParamValueString = matches ? matches[2] : '';
            const queryParamValues = queryParamValueString.split('|');
            for (const element of queryParamValues) {
                const queryParamRangeValues = element.split('~');
                if (queryParamRangeValues.length === 2) {
                    const v1 = queryParamRangeValues[0];
                    const v2 = queryParamRangeValues[1];
                    filters[queryParamName].push(new HibuRangeFilter(v1, v2));
                } else {
                    filters[queryParamName].push(new HibuEqualsFilter(element));
                }
            }
        }
    }
    return filters;
};

/**
 * Builds a string out of the fieldNames and the corresponding values of the selected facets
 * @param facets to get all the possible facetGroup names
 * @param filters filters, that need to be represented in the url
 * @return {string} selected facets as string for building the url
 */
export const buildFacetFilterString = (facets: HibuFacetGroup[], filters: HibuFacetFilter): string => {
    const stringParts: string[] = [];
    if (facets) {
        const fieldNamesWithActiveFilters = facets.map(group => group.fieldName).filter(elem => filters.hasOwnProperty(elem));
        fieldNamesWithActiveFilters.forEach((fieldName, index0) => {
            const filtersByGroup: HibuFilter[] = filters[fieldName];
            if (filtersByGroup) {
                stringParts.push(fieldName, '[');
                filtersByGroup?.forEach((elem: HibuFilter, index: number) => {
                    if (elem.type === EQUALS) {
                        elem = elem as HibuEqualsFilter;
                        stringParts.push(elem.invert ? '-' + elem.value : elem.value);
                        if (index !== filtersByGroup.length - 1) {
                            stringParts.push('|');
                        }
                    } else if (elem.type === RANGE) {
                        elem = elem as HibuRangeFilter;
                        stringParts.push(elem.gte, '~', elem.lt); // Hint: if multiple ranges will be supported in UI, this needs to be changed!
                    }
                });
                stringParts.push(']');
                if (index0 !== fieldNamesWithActiveFilters.length - 1) {
                    stringParts.push(QUERY_ENTRY_SEPARATOR);
                }
            }
        });
    }
    return stringParts.length > 0 ? parseURI(URL_FILTER_PARAM, stringParts.join(''), true) : '';
};
